<template>
  <div class="application">
    <div class="application__header">
      <div
          class="application__header__button"
          @click="returnToAllApps"
      ></div>
      <div class="application__header__title">
        {{ `${isEditMode ? localApp.title : 'Add Application'}` }}
      </div>
    </div>
    <div class="application__body">
      <div class="application__body__info info">
        <div class="info__item item">
          <div class="item__label">
            Platform
          </div>
          <VToggle
              v-if="!isEditMode"
              :checked="isIOS"
              label="Android/IOS"
              class="VToggle"
              is-vertical
              @click="changePlatform"
          ></VToggle>
        </div>
        <div class="info__item item">
          <div class="item__label">
            Name*
          </div>
          <VInput
              v-model="localApp.title"
              :class="{invalid: !validation.name}"
              @input="inputName"
          />
        </div>
        <div class="info__item item item_smallbiz">
          <div class="item__label">
            AppStore Small Business Program
          </div>
          <div 
              v-for="(period, index) in smallbizPeriods" 
              :key="index"
              class="item__dates-row"
          >
            <div class="date-field">
              <div class="date-field__label">Start Date</div>
              <VDateTimePicker
                  :dates="{start: period.start, end: null}"
                  :is-show-presets="false"
                  :range="false"
                  :solo="true"
                  type="date"
                  :format="'YYYY-MM-DD'"
                  :disabled-date="(date) => disabledStartDateForPeriod(date, index)"
                  @changeSolo="changeSmallbizPeriodDate($event, index, 'start')"
              />
            </div>
            <div class="date-field">
              <div class="date-field__label">End Date (optional)</div>
              <VDateTimePicker
                  :dates="{start: period.end, end: null}"
                  :is-show-presets="false"
                  :range="false"
                  :solo="true"
                  type="date"
                  :format="'YYYY-MM-DD'"
                  :disabled-date="(date) => disabledEndDateForPeriod(date, index)"
                  @changeSolo="changeSmallbizPeriodDate($event, index, 'end')"
              />
            </div>
            <div class="date-field-actions">
              <button
                  v-if="smallbizPeriods.length > 1"
                  type="button"
                  @click="removePeriod(index)"
                  class="remove-period-btn"
              >
                <font-awesome-icon icon="fa-solid fa-trash" />
              </button>
            </div>
          </div>
          <div class="add-period-row">
            <button
                type="button"
                :disabled="!canAddNewPeriod"
                @click="addNewPeriod"
                class="add-period-btn"
            >
              <span class="icon-plus"></span>
              <span class="text">Add period</span>
            </button>
          </div>
        </div>
        <div class="info__item item">
          <div class="item__label">
            Bundle ID (Package Name)*
          </div>
          <VInput
              v-model="localApp.bundle_id"
              :disabled="isEditMode"
              :class="{invalid: validation.bundle_id === false}"
              @input="inputUrl"
          />
        </div>
        <div
            class="info__item item item_shared-secret"
            v-if="isShowSecret"
        >
          <div class="item__label">
            AppStore Connect shared secret
          </div>
          <VInput
              :disabled="!isAllowEditSecret"
              v-model="localApp.secret"
          />
        </div>
        <div
            class="info__item item "
            v-if="isIOS"
        >
          <div class="item__label">
            Key id
          </div>
          <VInput
              v-model="localApp.key.key_id"
          />
        </div>
        <div
            class="info__item item "
            v-if="isIOS"
        >
          <div class="item__label">
            Issuer id
          </div>
          <VInput
              v-model="localApp.key.issuer_id"
          />
        </div>
        <div
            class="info__item item "
            v-if="isIOS"
        >
          <div class="item__label">
            Private key
          </div>
          <div class="item__input">
            <VInput
                disabled
                :value="localApp.key.private_key ? 'Added' : ''"
            />
            <input
                type="file"
                ref="fileInput"
                @change="handleFileUpload"
                style="display: none;"
            />
            <VButton
                theme="green"
                type="button"
                @click="addPrivateKey"
            >Add
            </VButton>
          </div>
        </div>
        <div class="item__buttons">
          <VButton
              type="button"
              theme="red"
              v-if="isEditMode"
              @click="initDeleteNotification"
          >
            Delete
          </VButton>
          <VButton
              type="button"
              theme="green"
              @click="saveChanges"
          >
            Save
          </VButton>
        </div>
      </div>
      <div
          v-if="!localApp.logo_large.length && !imageLoading"
          class="application__body__logo application__body__logo_empty"
      >
      </div>
      <div
          v-if="localApp.logo_large.length && !imageLoading"
          class="application__body__logo"
          :style='{backgroundImage:`url(${this.localApp.logo_large})`}'
      >
      </div>
      <div
          class="application__body__logo_loader"
          v-if="imageLoading"
      >
        <div class="loading-container">
          <div class="loading"></div>
        </div>
      </div>

    </div>


  </div>
</template>

<script>
import apiApps from '../../api/apiApps';
import {eventBus} from '@/main';
import images from './images.json';
import cookieLocalStorage from '../../helpers/dataStorage';

export default {
  name: 'Application',
  props: {
    isEditMode: {
      type: Boolean,
      required: true,
      default: false,
    },
    app: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  data() {
    return {
      isIOS: true,
      validation: {
        name: true,
        bundle_id: true,
        logo_large: false,
      },
      localApp: {},
      imageLoading: false,
      images,
      smallbizPeriods: [
        {
          start: '',
          end: '',
        },
      ],
    };
  },
  computed: {
    debouncedInputUrl() {
      const DELAY = 300;
      const self = this;
      return _.debounce(function () {
        self.getApplicationLogo();
      }, DELAY);
    },
    isAllowEditSecret() {
      return this.localApp.secret;
    },
    isShowSecret() {
      return this.isEditMode && this.isIOS;
    },
    canAddNewPeriod() {
      if (this.smallbizPeriods.length === 0) return true;
      const lastPeriod = this.smallbizPeriods[this.smallbizPeriods.length - 1];
      return lastPeriod.end && lastPeriod.end.length > 0;
    },
  },
  created() {
    this.localApp = _.cloneDeep(this.app);
    this.isIOS = this.localApp.os === 'ios' || !this.localApp.os;
    this.initializeSmallbizDates();
  },
  methods: {
    addPrivateKey() {
      this.$refs.fileInput.click();
    },
    changePlatform() {
      this.isIOS = !this.isIOS;
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      this.readFileAsBase64(file)
          .then((result) => {
            this.localApp.key.private_key = result;
          })
          .catch((err) => {
            console.error(err);
          });
    },
    readFileAsBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = event => {
          // Split the result at the first comma and take the second part
          let base64String = event.target.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = error => reject(error);
        reader.readAsDataURL(file);
      });
    },
    validateInfo() {
      if (this.isEditMode) {
        this.validation.logo_large = true;
      } else {
        this.validation.logo_large = !!this.localApp.logo_large.trim().length;
      }
      this.validation.bundle_id = !!this.localApp.bundle_id.trim().length;
      this.validation.name = !!this.localApp.title.trim().length;
      return this.validation.name && this.validation.bundle_id && this.validation.logo_large;
    },
    returnToAllApps() {
      this.$emit('returnToAllApps');
    },
    changeSmallbiz() {
      this.localApp.smallbiz = !this.localApp.smallbiz;
    },
    saveChanges() {
      if (this.validateInfo()) {
        const newApp = _.cloneDeep(this.localApp);
        newApp.os = this.isIOS ? 'ios' : 'android';
        this.$emit('saveChanges', newApp);
      }
    },
    initDeleteNotification() {
      if (this.isEditMode) {
        const notification = {};
        notification.confirmText = 'Delete';
        notification.data = this.localApp;
        notification.eventConfirm = 'VNotification-Delete-application';
        notification.contentText = 'This application will be deleted';
        notification.headerText = `Delete application ${this.localApp.title}?`;
        eventBus.$emit('VNotificationShow', notification);
      }
    },
    getApplicationLogo() {
      if (!this.isEditMode) {
        const sendObj = {
          bundle_id: this.localApp.bundle_id,
        };
        this.localApp.logo_large = '';
        this.$toastr.removeByType('error');
        this.imageLoading = true;
        return apiApps.getAppLogo(sendObj, this.isIOS).then((response) => {
          if (response.results.length) {
            const appLargeLogoUrl = response.results[0].artworkUrl512;
            const appSmallLogoUrl = response.results[0].artworkUrl100;
            this.localApp.logo_large = appLargeLogoUrl;
            this.localApp.logo_small = appSmallLogoUrl;
            this.validation.logo_large = true;
          } else {
            const appImageId = +cookieLocalStorage.get('currentApplicationImageId') || 0;
            this.localApp = Object.assign(this.localApp, images[appImageId]);
            this.validation.logo_large = true;
          }
          this.imageLoading = false;
        }).catch(() => {
          const appImageId = +cookieLocalStorage.get('currentApplicationImageId') || 0;
          this.localApp = Object.assign(this.localApp, images[appImageId]);
          this.validation.logo_large = true;
          this.imageLoading = false;
          // this.$toastr.e(reject);
        });
      }
    },
    inputUrl() {
      this.debouncedInputUrl();
      this.validation.bundle_id = !!this.localApp.bundle_id.trim().length;
    },
    inputName() {
      this.validation.name = !!this.localApp.title.trim().length;
    },
    initializeSmallbizDates() {
      if (this.localApp.smallbiz_history && this.localApp.smallbiz_history.length > 0) {
        this.smallbizPeriods = this.localApp.smallbiz_history.map(entry => ({
          start: entry.start || '',
          end: entry.end || '',
        }));
      } else {
        this.smallbizPeriods = [{ start: '', end: '' }];
      }
    },
    changeSmallbizPeriodDate(value, index, property) {
      if (value) {
        const dateStr = this.$moment(value).format('YYYY-MM-DD');
        this.smallbizPeriods[index][property] = dateStr + 'T00:00:00Z';
      } else {
        this.smallbizPeriods[index][property] = '';
      }
      this.updateSmallbizHistory();
    },
    disabledStartDateForPeriod(date, index) {
      if (index === 0) {
        return false;
      }
      const previousPeriod = this.smallbizPeriods[index - 1];
      if (!previousPeriod.end) {
        return true;
      }
      const previousEndDate = new Date(previousPeriod.end);
      previousEndDate.setHours(0, 0, 0, 0);
      return date < previousEndDate;
    },
    disabledEndDateForPeriod(date, index) {
      const period = this.smallbizPeriods[index];
      if (!period.start) {
        return false;
      }
      const startDate = new Date(period.start);
      startDate.setHours(0, 0, 0, 0);
      return date < startDate;
    },
    addNewPeriod() {
      this.smallbizPeriods.push({ start: '', end: '' });
    },
    removePeriod(index) {
      this.smallbizPeriods.splice(index, 1);
      this.updateSmallbizHistory();
    },
    updateSmallbizHistory() {
      const validPeriods = this.smallbizPeriods.filter(period => period.start);
      this.localApp.smallbiz_history = validPeriods.map(period => ({
        start: period.start,
        end: period.end || null,
      }));
    },
  },
};
</script>

<style
    scoped
    lang="scss"
>

.application {
  &__header {
    display: flex;
    align-items: center;
    margin-bottom: 26px;

    &__button {
      width: 15px;
      height: 8px;
      margin-right: 10px;
      cursor: pointer;
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
      background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyB3aWR0aD0iMTZweCIgaGVpZ2h0PSI4cHgiIHZpZXdCb3g9IjAgMCAxNiA4IiB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPgogICAgPHRpdGxlPjA4NDU3OEU5LUVGQzgtNDkxNy1BMEU1LTBGQ0UzMjMyQzZDNEAxLDV4PC90aXRsZT4KICAgIDxnIGlkPSJQYWdlLTQiIHN0cm9rZT0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIxIiBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPgogICAgICAgIDxnIGlkPSJNYWluX2FwcGxpY2F0aW9uc19uZXciIHRyYW5zZm9ybT0idHJhbnNsYXRlKC04MC4wMDAwMDAsIC02My4wMDAwMDApIiBmaWxsPSIjNjA4OUU3Ij4KICAgICAgICAgICAgPHBhdGggZD0iTTg0LjUzNTUzMzksNjMuNDY0NDY2MSBDODQuOTI2MDU4Miw2My44NTQ5OTA0IDg0LjkyNjA1ODIsNjQuNDg4MTU1NCA4NC41MzU1MzM5LDY0Ljg3ODY3OTcgTDgzLjQxMyw2NS45OTk1NzI5IEw5NC44Mjg0MjcxLDY2IEM5NS4zODA3MTE5LDY2IDk1LjgyODQyNzEsNjYuNDQ3NzE1MyA5NS44Mjg0MjcxLDY3IEM5NS44Mjg0MjcxLDY3LjU1MjI4NDcgOTUuMzgwNzExOSw2OCA5NC44Mjg0MjcxLDY4IEw4My40MTQsNjcuOTk5NTcyOSBMODQuNTM1NTMzOSw2OS4xMjEzMjAzIEM4NC45MjYwNTgyLDY5LjUxMTg0NDYgODQuOTI2MDU4Miw3MC4xNDUwMDk2IDg0LjUzNTUzMzksNzAuNTM1NTMzOSBDODQuMTQ1MDA5Niw3MC45MjYwNTgyIDgzLjUxMTg0NDYsNzAuOTI2MDU4MiA4My4xMjEzMjAzLDcwLjUzNTUzMzkgTDgwLjI5Mjg5MzIsNjcuNzA3MTA2OCBDNzkuOTAyMzY4OSw2Ny4zMTY1ODI1IDc5LjkwMjM2ODksNjYuNjgzNDE3NSA4MC4yOTI4OTMyLDY2LjI5Mjg5MzIgTDgzLjEyMTMyMDMsNjMuNDY0NDY2MSBDODMuNTExODQ0Niw2My4wNzM5NDE4IDg0LjE0NTAwOTYsNjMuMDczOTQxOCA4NC41MzU1MzM5LDYzLjQ2NDQ2NjEgWiIgaWQ9IkNvbWJpbmVkLVNoYXBlIj48L3BhdGg+CiAgICAgICAgPC9nPgogICAgPC9nPgo8L3N2Zz4=');
    }

    &__title {
      font-size: 16px;
      line-height: 20px;
      font-weight: 700;
      text-align: left;
    }
  }

  &__body {
    display: flex;

    .info {
      width: 370px;
      display: flex;
      flex-direction: column;
      margin-right: 40px;

      .item {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        margin-bottom: 16px;

        &__label {
          font-size: 16px;
          line-height: 20px;
          font-weight: 700;
          margin-bottom: 10px;
        }

        &__input {
          display: flex;

          ::v-deep {
            .VInput input {
              width: 220px !important;
            }

            .VButton {
              height: 26px;
              margin-left: 6px;
            }
          }
        }

        &__buttons {
          display: flex;
          justify-content: space-between;
        }

        &__checkbox {
          display: flex;
          align-items: center;

          &__label {
            margin-left: 10px;
          }
        }

        ::v-deep {
          .VInput input {
            font-family: Quicksand;
            color: $primary-color;
            font-size: 14px;
            line-height: 18px;
            width: 326px;
            height: 36px;
            padding-left: 20px;
          }
        }

        &_sdk {
          .item__label {
            margin-bottom: 12px;

            &_transparent {
              opacity: .3;
            }
          }
        }

        &_shared-secret {
          .item__label {
            margin-bottom: 14px;
          }
        }

        &_smallbiz {
          .item__label {
            margin-bottom: 10px;
          }
        }

        &__dates-row {
          display: flex;
          gap: 10px;
          align-items: flex-end;
          margin-bottom: 10px;

          .date-field {
            display: flex;
            flex-direction: column;

            &__label {
              font-size: 12px;
              line-height: 16px;
              font-weight: 600;
              margin-bottom: 6px;
              color: $primary-color;
            }

            ::v-deep {
              .mx-datepicker {
                width: 145px;
              }

              .mx-input-wrapper {
                width: 145px;
              }

              .mx-input {
                width: 145px !important;
                height: 36px;
                padding-left: 10px;
                font-family: Quicksand;
                color: $primary-color;
                font-size: 14px;
                line-height: 18px;
              }
            }
          }

          &-actions {
            display: flex;
            align-items: center;
            padding-bottom: 2px;

            .remove-period-btn {
              width: 36px;
              height: 36px;
              padding: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              background: transparent;
              border: none;
              cursor: pointer;
              transition: opacity 0.2s;
              color: #333333;
              font-size: 16px;

              &:hover {
                opacity: 0.7;
              }
            }
          }
        }

        .add-period-row {
          margin-top: 10px;
          margin-left: -8px;

          .add-period-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px;
            background: transparent;
            border: none;
            cursor: pointer;
            font-family: Quicksand;
            font-size: 14px;
            font-weight: 600;
            color: $primary-color;
            transition: opacity 0.2s;

            &:hover:not(:disabled) {
              opacity: 0.7;
            }

            &:disabled {
              opacity: 0.4;
              cursor: not-allowed;
            }

            .icon-plus {
              width: 20px;
              height: 20px;
              display: inline-block;
              background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTAiIGN5PSIxMCIgcj0iOSIgc3Ryb2tlPSIjNjA4OUU3IiBzdHJva2Utd2lkdGg9IjEuNSIvPgo8cGF0aCBkPSJNMTAgNlYxNE0xNCAxMEg2IiBzdHJva2U9IiM2MDg5RTciIHN0cm9rZS13aWR0aD0iMS41IiBzdHJva2UtbGluZWNhcD0icm91bmQiLz4KPC9zdmc+Cg==');
              background-size: contain;
              background-repeat: no-repeat;
              background-position: center;
            }

            .text {
              line-height: 1;
            }
          }
        }
      }

      .sdk {
        &__status {
          color: $negative-color;
        }

        margin-bottom: 25px;
      }

      ::v-deep {
        .VButton-button {
          width: 67px;
          margin-right: 20px;
          align-self: flex-end;
        }
      }
    }

    &__logo {
      margin-top: 30px;
      width: 267px;
      height: 267px;
      border-radius: 60px;
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;

      &_loader {
        display: flex;
        width: 300px;
        align-items: center;
        justify-content: center;
      }

      &_empty {
        background-image: url('data:image/svg+xml;base64,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');
      }
    }
  }
}

@keyframes rotate-loading {
  0% {
    transform: rotate(0deg);
    -ms-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    -moz-transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
    -ms-transform: rotate(360deg);
    -webkit-transform: rotate(360deg);
    -o-transform: rotate(360deg);
    -moz-transform: rotate(360deg);
  }
}

@-moz-keyframes rotate-loading {
  0% {
    transform: rotate(0deg);
    -ms-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    -moz-transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
    -ms-transform: rotate(360deg);
    -webkit-transform: rotate(360deg);
    -o-transform: rotate(360deg);
    -moz-transform: rotate(360deg);
  }
}

@-webkit-keyframes rotate-loading {
  0% {
    transform: rotate(0deg);
    -ms-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    -moz-transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
    -ms-transform: rotate(360deg);
    -webkit-transform: rotate(360deg);
    -o-transform: rotate(360deg);
    -moz-transform: rotate(360deg);
  }
}

@-o-keyframes rotate-loading {
  0% {
    transform: rotate(0deg);
    -ms-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    -moz-transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
    -ms-transform: rotate(360deg);
    -webkit-transform: rotate(360deg);
    -o-transform: rotate(360deg);
    -moz-transform: rotate(360deg);
  }
}

@keyframes rotate-loading {
  0% {
    transform: rotate(0deg);
    -ms-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    -moz-transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
    -ms-transform: rotate(360deg);
    -webkit-transform: rotate(360deg);
    -o-transform: rotate(360deg);
    -moz-transform: rotate(360deg);
  }
}

@-moz-keyframes rotate-loading {
  0% {
    transform: rotate(0deg);
    -ms-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    -moz-transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
    -ms-transform: rotate(360deg);
    -webkit-transform: rotate(360deg);
    -o-transform: rotate(360deg);
    -moz-transform: rotate(360deg);
  }
}

@-webkit-keyframes rotate-loading {
  0% {
    transform: rotate(0deg);
    -ms-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    -moz-transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
    -ms-transform: rotate(360deg);
    -webkit-transform: rotate(360deg);
    -o-transform: rotate(360deg);
    -moz-transform: rotate(360deg);
  }
}

@-o-keyframes rotate-loading {
  0% {
    transform: rotate(0deg);
    -ms-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    -moz-transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
    -ms-transform: rotate(360deg);
    -webkit-transform: rotate(360deg);
    -o-transform: rotate(360deg);
    -moz-transform: rotate(360deg);
  }
}

@keyframes loading-text-opacity {
  0% {
    opacity: 0
  }
  20% {
    opacity: 0
  }
  50% {
    opacity: 1
  }
  100% {
    opacity: 0
  }
}

@-moz-keyframes loading-text-opacity {
  0% {
    opacity: 0
  }
  20% {
    opacity: 0
  }
  50% {
    opacity: 1
  }
  100% {
    opacity: 0
  }
}

@-webkit-keyframes loading-text-opacity {
  0% {
    opacity: 0
  }
  20% {
    opacity: 0
  }
  50% {
    opacity: 1
  }
  100% {
    opacity: 0
  }
}

@-o-keyframes loading-text-opacity {
  0% {
    opacity: 0
  }
  20% {
    opacity: 0
  }
  50% {
    opacity: 1
  }
  100% {
    opacity: 0
  }
}

.loading-container, .loading {
  height: 100px;
  position: relative;
  width: 100px;
  border-radius: 100%;
}


.loading {
  border: 2px solid transparent;
  border-color: transparent $primary-color transparent $primary-color;
  -moz-animation: rotate-loading 1.5s linear 0s infinite normal;
  -moz-transform-origin: 50% 50%;
  -o-animation: rotate-loading 1.5s linear 0s infinite normal;
  -o-transform-origin: 50% 50%;
  -webkit-animation: rotate-loading 1.5s linear 0s infinite normal;
  -webkit-transform-origin: 50% 50%;
  animation: rotate-loading 1.5s linear 0s infinite normal;
  transform-origin: 50% 50%;
}

.loading-container:hover .loading {
  border-color: transparent #E45635 transparent #E45635;
}

.loading-container:hover .loading,
.loading-container .loading {
  -webkit-transition: all 0.5s ease-in-out;
  -moz-transition: all 0.5s ease-in-out;
  -ms-transition: all 0.5s ease-in-out;
  -o-transition: all 0.5s ease-in-out;
  transition: all 0.5s ease-in-out;
}

</style>
